/**
 * The approved personal Financial status: the user's saved, revision-bound
 * recalculation status (`GET /api/me/financial-updates/current`) and its SSE
 * stream. It only reports. A completed publication refreshes the mounted saved
 * views once; nothing here polls or starts a calculation.
 *
 * One stream per account is shared across visible documents of the same origin
 * (Web Locks elect a leader, a BroadcastChannel shares its snapshots), as in
 * the workbench. Reconnects back off; after the last delay the status is
 * reported unavailable until a refresh.
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, ApiError } from './http'
import { resolve } from './endpoints'
import { keys } from './queries'
import type { CurrentUser } from './types'

export type FinancialUpdateState = 'idle' | 'scheduled' | 'running' | 'completed' | 'failed'

export interface FinancialUpdateStatus {
  contractVersion: 'financial-update-status-v1'
  state: FinancialUpdateState
  runId: string | null
  revision: string
  sequence: string
  scheduledFor: string | null
  startedAt: string | null
  finishedAt: string | null
  progress: { completed: number; total: number | null }
  error: string | null
  observedAt: string
}

export type FinancialUpdateConnection = 'connecting' | 'live' | 'reconnecting' | 'unavailable'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const DECIMAL_PATTERN = /^(0|[1-9][0-9]{0,18})$/
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/
const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000]
const STABLE_CONNECTION_MS = 30_000

function mismatch(message: string): never { throw new ApiError(502, 'financial_updates_contract_mismatch', message) }
function record(value: unknown, name: string): Record<string, unknown> { if (!value || typeof value !== 'object' || Array.isArray(value)) mismatch(`${name} must be an object`); return value as Record<string, unknown> }
function decimal(value: unknown, name: string): string { if (typeof value !== 'string' || !DECIMAL_PATTERN.test(value)) mismatch(`${name} must be a non-negative decimal string`); return value }
function timestamp(value: unknown, name: string): string { if (typeof value !== 'string' || !TIMESTAMP_PATTERN.test(value) || Number.isNaN(Date.parse(value))) mismatch(`${name} must be an ISO timestamp`); return value }
function nullableTimestamp(value: unknown, name: string): string | null { return value === null ? null : timestamp(value, name) }
function count(value: unknown, name: string): number { if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) mismatch(`${name} must be a non-negative integer`); return value }

export function parseFinancialUpdateEnvelope(value: unknown): FinancialUpdateStatus {
  const data = record(record(value, 'Response').data, 'Financial update')
  if (data.contractVersion !== 'financial-update-status-v1') mismatch('Unsupported Financial update contract')
  if (!['idle', 'scheduled', 'running', 'completed', 'failed'].includes(String(data.state))) mismatch('Invalid Financial update state')
  if (data.runId !== null && (typeof data.runId !== 'string' || !UUID_PATTERN.test(data.runId))) mismatch('Invalid Financial update run')
  if (data.error !== null && (typeof data.error !== 'string' || data.error.length > 8_192)) mismatch('Invalid Financial update error')
  const progress = record(data.progress, 'Financial update progress')
  const completed = count(progress.completed, 'Completed results')
  const total = progress.total === null ? null : count(progress.total, 'Total results')
  if (total !== null && completed > total) mismatch('Completed results exceed the total')
  return {
    contractVersion: 'financial-update-status-v1', state: data.state as FinancialUpdateState, runId: data.runId as string | null,
    revision: decimal(data.revision, 'Financial update revision'), sequence: decimal(data.sequence, 'Financial update sequence'),
    scheduledFor: nullableTimestamp(data.scheduledFor, 'Scheduled time'), startedAt: nullableTimestamp(data.startedAt, 'Start time'), finishedAt: nullableTimestamp(data.finishedAt, 'Finish time'),
    progress: { completed, total }, error: data.error as string | null, observedAt: timestamp(data.observedAt, 'Observation time'),
  }
}

/** Decimal strings keep the server's full ordering range without rounding. */
export function newerFinancialUpdate(current: FinancialUpdateStatus | undefined, next: FinancialUpdateStatus): FinancialUpdateStatus {
  if (!current) return next
  if (BigInt(next.sequence) <= BigInt(current.sequence) || BigInt(next.revision) < BigInt(current.revision)) return current
  return next
}

export const financialUpdatesKey = (actorId: string) => ['financialUpdates', actorId] as const

const actorIsCurrent = (client: QueryClient, actorId: string) => client.getQueryData<CurrentUser>(keys.me)?.id === actorId

/** Saved views refreshed once per completed publication (not on duplicates or old completions). */
const SAVED_VIEW_PREFIXES: ReadonlyArray<readonly unknown[]> = [['calculationCards'], ['propertyMarkers'], ['dealsV2'], ['refurbishment'], ['areaPotential']]

function useFinancialUpdatesSnapshot(actorId: string, enabled: boolean) {
  const client = useQueryClient()
  const queryKey = financialUpdatesKey(actorId)
  return useQuery({
    queryKey,
    enabled: enabled && actorId.length > 0,
    queryFn: async ({ signal }) => {
      const next = parseFinancialUpdateEnvelope(await api<unknown>('financialUpdatesCurrent', { signal }))
      if (!actorIsCurrent(client, actorId)) throw new ApiError(401, 'authentication_changed', 'Your signed-in account changed. Reload to check your results.')
      // A late read must not overwrite an event accepted while it was in flight.
      return newerFinancialUpdate(client.getQueryData<FinancialUpdateStatus>(queryKey), next)
    },
    staleTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/** Reads the saved status and follows its stream while mounted. Reads and events never calculate. */
export function useFinancialUpdateStatus(actorId: string, enabled: boolean) {
  const client = useQueryClient()
  const [stream, setStream] = useState<{ actorId: string; connection: FinancialUpdateConnection; error: string | null; accessDenied: boolean }>({ actorId, connection: 'connecting', error: null, accessDenied: false })
  const accessDenied = stream.actorId === actorId && stream.accessDenied
  const activeActor = enabled && !accessDenied && actorId.length > 0
  const current = useFinancialUpdatesSnapshot(actorId, activeActor)
  const hasSnapshot = current.data !== undefined

  useEffect(() => {
    if (!accessDenied) return
    const queryKey = financialUpdatesKey(actorId)
    void client.cancelQueries({ queryKey, exact: true })
    client.removeQueries({ queryKey, exact: true })
  }, [actorId, accessDenied, client])

  useEffect(() => {
    if (!activeActor || !(current.error instanceof ApiError) || current.error.status !== 403) return
    // A saved-status read can discover revocation while the stream is closed.
    setStream({ actorId, connection: 'unavailable', error: current.error.message, accessDenied: true })
  }, [actorId, activeActor, current.error])

  useEffect(() => {
    if (!activeActor || !hasSnapshot) return
    let disposed = false
    let source: EventSource | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let retries = 0
    let openedAt: number | null = null
    let visibilityGeneration = 0
    let visible = document.visibilityState === 'visible'
    let leader = false
    let blocked = false
    let lockAbort: AbortController | null = null
    let releaseLock: (() => void) | null = null
    let channel: BroadcastChannel | null = null
    let connectionState: FinancialUpdateConnection = 'connecting'
    let connectionError: string | null = null
    const queryKey = financialUpdatesKey(actorId)
    const initialStatus = client.getQueryData<FinancialUpdateStatus>(queryKey)
    let lastCompletedRevision = initialStatus?.state === 'completed' ? initialStatus.revision : null
    const coordinatorName = `auction-hub:financial-updates:v1:${actorId}`
    const senderId = crypto.randomUUID()
    const canCoordinate = typeof navigator.locks?.request === 'function' && typeof BroadcastChannel === 'function'
    const unavailableMessage = 'Live results updates are unavailable. Refresh to check the latest status.'

    const isVisibleActor = () => !disposed && visible && document.visibilityState === 'visible' && actorIsCurrent(client, actorId)
    const post = (message: Record<string, unknown>) => {
      if (disposed || !actorIsCurrent(client, actorId)) return
      channel?.postMessage({ contractVersion: 'financial-update-share-v1', actorId, senderId, ...message })
    }
    const publish = (targetId?: string) => {
      if (!leader || !isVisibleActor()) return
      post({ kind: 'snapshot', targetId, snapshot: client.getQueryData<FinancialUpdateStatus>(queryKey), connection: connectionState, error: connectionError })
    }
    const updateConnection = (connection: FinancialUpdateConnection, error: string | null = null) => {
      connectionState = connection
      connectionError = error
      if (!disposed && actorIsCurrent(client, actorId)) setStream({ actorId, connection, error, accessDenied: false })
      publish()
    }
    const close = () => { source?.close(); source = null }
    const releaseLeadership = () => {
      visibilityGeneration += 1
      if (retryTimer !== null) { clearTimeout(retryTimer); retryTimer = null }
      close()
      openedAt = null
      lockAbort?.abort()
      lockAbort = null
      releaseLock?.()
      releaseLock = null
      leader = false
    }
    const unavailable = (message = unavailableMessage, broadcast = leader) => {
      if (broadcast) post({ kind: 'unavailable', error: message })
      blocked = true
      releaseLeadership()
      updateConnection('unavailable', message)
    }
    const denyAccess = (message: string, broadcast = true) => {
      if (broadcast) post({ kind: 'denied', error: message })
      blocked = true
      releaseLeadership()
      void client.cancelQueries({ queryKey, exact: true })
      client.removeQueries({ queryKey, exact: true })
      if (!disposed && actorIsCurrent(client, actorId)) setStream({ actorId, connection: 'unavailable', error: message, accessDenied: true })
    }
    const refreshCompletedViews = (previous: FinancialUpdateStatus | undefined, next: FinancialUpdateStatus) => {
      if (!previous || newerFinancialUpdate(previous, next) !== next || next.state !== 'completed' || lastCompletedRevision === next.revision) return
      lastCompletedRevision = next.revision
      void Promise.allSettled(SAVED_VIEW_PREFIXES.map(prefix => client.invalidateQueries({ queryKey: prefix })))
    }
    const acceptSnapshot = (next: FinancialUpdateStatus) => {
      const previous = client.getQueryData<FinancialUpdateStatus>(queryKey)
      const latest = newerFinancialUpdate(previous, next)
      client.setQueryData<FinancialUpdateStatus>(queryKey, latest)
      if (latest === next) refreshCompletedViews(previous, next)
    }
    const connect = () => {
      if (!leader || blocked || !isVisibleActor()) return
      updateConnection(retries === 0 ? 'connecting' : 'reconnecting')
      try {
        source = new EventSource(resolve('financialUpdatesEvents'), { withCredentials: true })
      } catch {
        reconnect()
        return
      }
      const connection = source
      connection.onopen = () => {
        if (!leader || source !== connection || !isVisibleActor()) { connection.close(); return }
        openedAt = Date.now()
        updateConnection('live')
      }
      connection.addEventListener('financial-update', (event) => {
        if (!leader || source !== connection || !isVisibleActor()) { connection.close(); return }
        try {
          const message = event as MessageEvent<string>
          const next = parseFinancialUpdateEnvelope(JSON.parse(message.data) as unknown)
          if (message.lastEventId && message.lastEventId !== next.sequence) mismatch('Financial update event ordering does not match its snapshot')
          acceptSnapshot(next)
          updateConnection('live')
        } catch {
          unavailable('Live results updates could not be read. Refresh to check the latest status.')
        }
      })
      connection.addEventListener('financial-update-access-denied', () => {
        if (disposed || source !== connection || !actorIsCurrent(client, actorId)) return
        denyAccess('Your access to results updates changed. Refresh to check your workspace.')
      })
      connection.onerror = () => { if (!disposed && source === connection) reconnect() }
    }
    const reconnect = () => {
      close()
      if (!leader || blocked || !isVisibleActor()) return
      if (openedAt !== null && Date.now() - openedAt >= STABLE_CONNECTION_MS) retries = 0
      openedAt = null
      const delay = RECONNECT_DELAYS_MS[retries]
      if (delay === undefined) { unavailable(); return }
      retries += 1
      updateConnection('reconnecting')
      // Transient errors retry only the event stream, within the same backoff.
      retryTimer = setTimeout(() => { retryTimer = null; connect() }, delay)
    }
    const refreshSnapshot = async (generation: number) => {
      const previous = client.getQueryData<FinancialUpdateStatus>(queryKey)
      // A visible resume or leadership handover reads the saved status once; no refresh loop.
      const result = await current.refetch({ cancelRefetch: false })
      if (generation !== visibilityGeneration || !isVisibleActor()) return false
      if (!result.isSuccess) {
        if (result.error instanceof ApiError && result.error.status === 403) denyAccess(result.error.message)
        else unavailable(result.error?.message ?? unavailableMessage)
        return false
      }
      refreshCompletedViews(previous, result.data)
      publish()
      return true
    }
    const join = () => {
      if (blocked || lockAbort !== null || !isVisibleActor()) return
      if (channel === null) { unavailable(); return }
      const controller = new AbortController()
      lockAbort = controller
      const generation = visibilityGeneration
      const valid = () => lockAbort === controller && generation === visibilityGeneration && !controller.signal.aborted && !blocked && isVisibleActor()
      const hold = (catchUp: boolean) => {
        if (!valid()) return
        leader = true
        retries = 0
        const held = new Promise<void>(resolveHeld => { releaseLock = resolveHeld })
        if (catchUp) {
          updateConnection('connecting')
          void refreshSnapshot(generation).then(fresh => { if (fresh && valid()) connect() })
        } else connect()
        // Holding the lock across bounded reconnects keeps other windows from opening a parallel stream.
        return held
      }
      post({ kind: 'hello' })
      void navigator.locks.request(coordinatorName, { ifAvailable: true }, lock => {
        if (lock) return hold(false)
        if (!valid()) return
        return navigator.locks.request(coordinatorName, { signal: controller.signal }, () => hold(true))
      }).catch((error: unknown) => {
        if (!controller.signal.aborted && valid()) unavailable(error instanceof Error ? error.message : unavailableMessage, false)
      })
    }

    if (canCoordinate) {
      try { channel = new BroadcastChannel(coordinatorName) } catch { /* reported as unavailable below */ }
    }
    if (channel !== null) channel.onmessage = event => {
      if (disposed || !actorIsCurrent(client, actorId)) return
      try {
        const message = record(event.data, 'Shared Financial update')
        if (message.contractVersion !== 'financial-update-share-v1' || message.actorId !== actorId || typeof message.senderId !== 'string' || !UUID_PATTERN.test(message.senderId) || message.senderId === senderId) return
        if (message.targetId !== undefined && message.targetId !== senderId) return
        if (message.kind === 'hello') { publish(message.senderId); return }
        if (message.error !== null && message.error !== undefined && (typeof message.error !== 'string' || message.error.length > 8_192)) return
        if (message.kind === 'denied' && typeof message.error === 'string') { denyAccess(message.error, false); return }
        if (!isVisibleActor()) return
        if (message.kind === 'unavailable' && typeof message.error === 'string') { unavailable(message.error, false); return }
        if (message.kind !== 'snapshot' || leader || blocked || !['connecting', 'live', 'reconnecting', 'unavailable'].includes(String(message.connection))) return
        acceptSnapshot(parseFinancialUpdateEnvelope({ data: message.snapshot }))
        updateConnection(message.connection as FinancialUpdateConnection, typeof message.error === 'string' ? message.error : null)
      } catch { /* malformed cross-document messages are ignored; server snapshots stay authoritative */ }
    }

    const visibilityChanged = () => {
      const nextVisible = document.visibilityState === 'visible'
      if (nextVisible === visible) return
      visible = nextVisible
      releaseLeadership()
      if (!visible || disposed || !actorIsCurrent(client, actorId)) return
      blocked = false
      updateConnection('connecting')
      const generation = visibilityGeneration
      void refreshSnapshot(generation).then(fresh => { if (fresh && generation === visibilityGeneration && isVisibleActor()) join() })
    }

    document.addEventListener('visibilitychange', visibilityChanged)
    if (visible) join()
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', visibilityChanged)
      releaseLeadership()
      channel?.close()
    }
  }, [actorId, activeActor, hasSnapshot, current.refetch, client])

  return {
    ...current,
    data: activeActor ? current.data : undefined,
    connection: stream.actorId === actorId ? stream.connection : 'connecting' as FinancialUpdateConnection,
    streamError: stream.actorId === actorId ? stream.error : null,
  }
}
